'use client';

import React, { useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { WayfinderProps } from './types';
import { Breadcrumb, NavItem, PatternPillCluster, PingerVariant } from 'mmds';
import {
    ContentStructureGender,
    ContentStructureNavItem,
    useContentStructure,
} from '@app/lib/content-structure';
import { useDragScroll } from '@app/lib/hooks/use-drag-scroll';
import localisePath from '@app/lib/helpers/localise-path';
import { Language } from '@app/lib/store-context/Context';
import Linker from '@app/components/linker/linker';
import cn from 'clsx';
import s from './collection-wayfinder.module.css';

type ContentStructureNavItemWithStatus = ContentStructureNavItem & {
    active?: boolean;
};

const CollectionWayfinder = ({
    contentStructure,
    activeItem,
    variant = 'default',
}: WayfinderProps) => {
    const router = useRouter();
    const { lang } = useParams();
    const { getPathsForNode } = useContentStructure(contentStructure);
    const basePath = getPathsForNode(activeItem);
    const baseNode = basePath[basePath.length - 1];
    const ref =
        useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
    const { events } = useDragScroll(ref);

    // If the collection is not in the content structure at all, none of the wayfinder can be generated.
    if (!baseNode) return null;

    let tier = baseNode;
    let siblingMode = false;

    // Work out which navigation tier to use in the Wayfinder. If the starting node has children, we can use it,
    // otherwise go up one level, and show a list of siblings of the starting node instead.
    if (!tier.children) {
        tier = basePath[basePath.length - 2];
        siblingMode = true;
    }

    let items: ContentStructureNavItemWithStatus[] =
        tier.children as ContentStructureNavItem[];
    if (siblingMode) {
        // Move the current sibling to the front.
        items = items
            ?.filter((item) => item.label !== baseNode.label)
            .filter(
                (item) => item.label !== 'All' && item.label !== 'Discover'
            );
        items?.unshift({ ...baseNode, active: true });
    }

    const pillCluster = items?.map((item, index) => {
        if (index === 0) {
            return {
                label: item.label, // TODO: Add count to label
                componentType: 'pinger' as 'pinger',
                pingerVariant: 'ghost' as PingerVariant,
                inactive: true,
            };
        }

        return {
            label: item.label,
            active: item.label === activeItem?.label,
            componentType: 'pinger' as 'pinger',
            pingerVariant: 'alt' as PingerVariant,
        };
    });

    if (variant === 'pill' && pillCluster) {
        return (
            <PatternPillCluster
                cluster={pillCluster}
                lang={lang as Language}
                nowrap
            />
        );
    }

    // Sometimes, a collection is in multiple navigation branches.
    // In this scenario we prefer the Unisex branch if it exists.
    // Otherwise, we'll default to the first of the other branches.
    const pathBranches = {
        unisex: basePath.filter(
            (navItem: ContentStructureNavItem) =>
                navItem.gender === ContentStructureGender.UNISEX
        ),
        man: basePath.filter(
            (navItem: ContentStructureNavItem) =>
                navItem.gender === ContentStructureGender.MAN
        ),
        woman: basePath.filter(
            (navItem: ContentStructureNavItem) =>
                navItem.gender === ContentStructureGender.WOMAN
        ),
    };

    let branchForBreadcrumb = pathBranches.woman;
    if (pathBranches.unisex.length) {
        branchForBreadcrumb = pathBranches.unisex;
    } else if (pathBranches.man.length) {
        branchForBreadcrumb = pathBranches.man;
    }

    if (branchForBreadcrumb.length > 3) {
        // If there's more than three nodes, it's entered some wierd territory.
        // So here is a nice hack until the collection metafields are implemented
        // properly to fix this.
        // If there's 9 then it's probably in MAN, Woman and Accessories..
        if (branchForBreadcrumb.length === 9) {
            branchForBreadcrumb = branchForBreadcrumb.slice(6, 9);
        }

        // Archive sale is featured in many places in the nav structure.
        // /ncollections/archive-sale-man
        if (branchForBreadcrumb.length === 8) {
            branchForBreadcrumb = branchForBreadcrumb.slice(0, 3);
        }

        // If there's 6, and it's in MAN and Woman and should become unisex.
        if (
            branchForBreadcrumb.length === 6 &&
            branchForBreadcrumb[0].gender !== branchForBreadcrumb[2].gender
        ) {
            branchForBreadcrumb = branchForBreadcrumb.slice(0, 3);
            branchForBreadcrumb[0] = {
                label: 'Accessories',
                hrefLink: '/collections/cycling-accessories',
                gender: ContentStructureGender.UNISEX,
            };
        }

        // Collections all-man and all-women also have an issue where they get
        // two different paths from the main nav because they are also in
        // collections
        const duplicateIndex = branchForBreadcrumb.findIndex((item, index) => {
            if (index === 0) return false;
            return item.label === branchForBreadcrumb[0].label;
        });
        if (duplicateIndex) {
            branchForBreadcrumb = branchForBreadcrumb.slice(0, duplicateIndex);
        }
    }

    return (
        <div className={s.wayfinder}>
            {basePath && (
                <Breadcrumb
                    path={branchForBreadcrumb}
                    lang={lang as Language}
                    Linker={Linker}
                />
            )}
            {items && baseNode && (
                <div className={s.root} {...events} ref={ref}>
                    {!siblingMode && (
                        <Linker
                            className={s.wayfinderItems}
                            href={localisePath(
                                baseNode.hrefLink as string,
                                lang as Language
                            )}
                            lang={lang as Language}
                            title={`Link to ${baseNode.label}`}
                        >
                            <div
                                className={cn(
                                    s.wayfinderItem,
                                    s.wayfinderSelectedItem
                                )}
                            >
                                All
                            </div>
                        </Linker>
                    )}
                    <div className={s.wayfinderItems}>
                        {items.map((item) => {
                            if (!item.hrefLink) return null;
                            return (
                                <NavItem
                                    key={item.label}
                                    label={`${item.label}`}
                                    count={
                                        item.label === 'All'
                                            ? undefined
                                            : item.count
                                    }
                                    className={s.wayfinderItem}
                                    active={item?.active}
                                    onSelect={() => {
                                        router.push(
                                            localisePath(
                                                item.hrefLink as string,
                                                lang as Language
                                            ) as string
                                        );
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionWayfinder;
